/**
 * ArchiveDrawer — the header-chrome **Arkisto** entry point + Sheet
 * drawer for archived (retired) content (024-cms US6 / FR-024; the
 * D-122 scope's "Arkisto drawer [US6 surface]").
 *
 * Proto parity (AdminCms2Page @ 9393e6f8): an outline pill "Arkisto"
 * button in the admin header opens a right-edge sheet titled "Arkisto"
 * listing the deleted rows, each as a card with title, type + deleted-at
 * meta, and a "Palauta" restore button. Restore returns the row to
 * drafts (`retired → draft`, the draft seeded server-side from the last
 * published revision) — announced via the proto's toast copy, here an
 * `aria-live` status line.
 *
 * Deliberate deviations (documented, lead ruling Phase 8):
 *
 *   - The drawer description keeps ONLY the proto's first sentence
 *     ("Poistetut rivit.") — the proto's "poista lopullisesti" + 1-month
 *     auto-purge sentences describe purge/retention capabilities omaplus
 *     explicitly defers (FR-026; purge is blocked work pending the
 *     retention/DPIA position).
 *   - NO purge affordance renders in any form — not disabled, not
 *     placeholder (P7 absent-not-faked; the proto's per-row "Poista
 *     lopullisesti" button has no omaplus backend and never will in MVP).
 *
 * Data wiring: its own `useAdminContentList({ includeRetired: true, locale })`
 * query (a separate cache entry from the catalog's default list — the
 * key carries the flag), filtered to `state === 'retired'`. The list
 * mounts only while the sheet is open (Radix unmounts closed content),
 * so the admin surface pays the wider query only on demand. The
 * restore mutation's success-side invalidation sweeps the whole list
 * family, so the restored row leaves this drawer and appears in the
 * catalog's Luonnokset tab on the next read.
 *
 * Mounted in `AdminCmsLayout`'s header band (the proto's placement,
 * beside the Päävalikko exit) — the layout renders inside the
 * composition root's `AdminTrpcProvider` / `QueryClientProvider`, so
 * the hooks resolve. Admin-gating is structural: the layout only wraps
 * `/admin/cms/*` routes, which the registry guards with
 * `role: 'admin'`.
 */

import { type JSX, useState } from 'react';
import { fill } from '../../i18n/fill.js';
import { useLanguage } from '../../i18n/useLanguage.js';
import { useTranslate } from '../../i18n/useTranslate.js';
import { Button } from '../../primitives/Button.js';
import { Card } from '../../primitives/Card.js';
import { Sheet } from '../../primitives/Sheet.js';
import { RestoreIcon } from '../../shell/icons.js';
import {
  type AdminContentListItem,
  useAdminContentList,
  useRestoreFromRetire,
} from './hooks/useAdminContent.js';
import { shapeTypeLabel } from './shapeTypeLabel.js';
import { useContentLocale } from './useContentLocale.js';

const sheetStackClasses = 'mt-4 flex flex-col gap-2 overflow-y-auto';
const itemCardClasses = 'flex items-start justify-between gap-2 p-3';
const itemBodyClasses = 'flex min-w-0 flex-1 flex-col gap-0.5';
const itemTitleClasses = 'truncate text-sm font-medium text-foreground';
const itemMetaClasses = 'm-0 text-xs text-muted-foreground';
const restoreButtonContentClasses = 'mr-1 h-3.5 w-3.5';
const stateTextClasses = 'm-0 mt-4 text-sm text-muted-foreground';
const errorLineClasses = 'm-0 mt-3 text-sm text-destructive';

interface ArchiveListProps {
  readonly onRestored: () => void;
  readonly onRestoreError: () => void;
}

/**
 * The drawer's body — split out so the `includeRetired: true` query
 * mounts only while the sheet is open (this component lives inside
 * `Sheet.Content`, which Radix unmounts when closed).
 *
 * Restore concurrency (review H1+M2): the pending disable is PER-ROW
 * (`pendingIds`), never the mutation's drawer-wide `isPending` — that
 * would lock every other row's Palauta during one restore. And a
 * successfully restored row is optimistically REMOVED (`restoredIds`)
 * rather than left rendered: the mutation settles before the
 * invalidation refetch drops the row from the payload, and in that
 * window a re-enabled button would let a rapid second click fire
 * `restoreFromRetire` on a now-draft item (a spurious `not-retired`
 * error right after success). The refetch then removes the row from
 * the payload itself; the set just bridges the gap.
 */
function ArchiveList({ onRestored, onRestoreError }: ArchiveListProps): JSX.Element {
  const t = useTranslate();
  // BOTH axes, and this is the one site that needs both. The UI language
  // decides how a DATE is written for the person reading it; the content locale
  // decides WHICH LANGUAGE'S rows the drawer lists. Collapsing them would make
  // an English-labelled tool write Finnish-formatted dates whenever the author
  // happened to be browsing Finnish content.
  const { active } = useLanguage();
  const { contentLocale } = useContentLocale();
  // Locale-derived date formatting per the `Messages.tsx` precedent:
  // `en` → en-GB, everything else (fi today, future locales until they
  // get an explicit mapping) → fi-FI.
  const dateLocale = active === 'en' ? 'en-GB' : 'fi-FI';
  // Filtered to the language this surface is DISPLAYING, as the catalog is
  // (043 US3). The procedure returns every language when asked without one, so
  // with a second language present an unfiltered drawer would list retired rows
  // the catalog never showed and offer to restore them into a list the author
  // is not looking at. `contentLocale` is the same value the catalog passes, so the two
  // cannot describe different languages.
  const query = useAdminContentList({ includeRetired: true, locale: contentLocale });
  const restore = useRestoreFromRetire();
  // A SET, not a single id (review D1): restores may overlap — a string
  // marker gets overwritten by the second click (re-enabling the first
  // row mid-flight) and nulled by whichever settles first (clearing the
  // other row's disable while it is still in flight). Functional
  // updates keep concurrent add/delete pairs from racing each other.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [restoredIds, setRestoredIds] = useState<ReadonlySet<string>>(new Set());

  if (query.isPending) {
    return <p className={stateTextClasses}>{t('cms.archive.loading')}</p>;
  }
  if (query.isError) {
    return (
      <p role="alert" className={errorLineClasses}>
        {t('cms.archive.errorState')}
      </p>
    );
  }

  const retired = (query.data ?? []).filter(
    (item) => item.state === 'retired' && !restoredIds.has(item.id),
  );
  if (retired.length === 0) {
    return <p className={stateTextClasses}>{t('cms.archive.empty')}</p>;
  }

  const clearPending = (id: string): void => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const onRestore = (item: AdminContentListItem): void => {
    setPendingIds((prev) => new Set(prev).add(item.id));
    restore.mutate(
      { id: item.id },
      {
        onSuccess: () => {
          clearPending(item.id);
          setRestoredIds((prev) => new Set(prev).add(item.id));
          onRestored();
        },
        onError: () => {
          clearPending(item.id);
          onRestoreError();
        },
      },
    );
  };

  return (
    <div className={sheetStackClasses} data-testid="cms-archive-list">
      {retired.map((item) => {
        // The retire transition bumps `updated_at` (and nothing later can
        // touch a retired row), so it carries the proto's deleted-at meta.
        const deletedAt = new Date(item.updatedAt).toLocaleString(dateLocale);
        return (
          <Card
            key={item.id}
            className={itemCardClasses}
            data-testid={`cms-archive-item-${item.id}`}
          >
            <div className={itemBodyClasses}>
              <span className={itemTitleClasses}>{item.title}</span>
              <p className={itemMetaClasses}>
                {shapeTypeLabel(t, item.shape)} ·{' '}
                {fill(t('cms.archive.deletedAt'), { date: deletedAt })}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              shape="pill"
              onClick={() => onRestore(item)}
              disabled={pendingIds.has(item.id)}
              data-testid={`cms-archive-restore-${item.id}`}
            >
              <RestoreIcon className={restoreButtonContentClasses} />
              {t('cms.archive.restore')}
            </Button>
          </Card>
        );
      })}
    </div>
  );
}

/**
 * The "Arkisto" trigger + drawer. The `aria-live` status line lives
 * inside the sheet next to the list (focus is trapped there while the
 * drawer is open, so the announcement reads in place); a fresh open
 * clears any stale announcement from the previous session.
 */
export function ArchiveDrawer(): JSX.Element {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<{ readonly kind: 'idle' } | { readonly kind: 'error' }>({
    kind: 'idle',
  });

  return (
    <Sheet.Root
      open={open}
      onOpenChange={(next) => {
        // A fresh open starts a clean session: drop a stale announcement.
        if (next) setStatus({ kind: 'idle' });
        setOpen(next);
      }}
    >
      <Sheet.Trigger asChild>
        <Button variant="outline" size="xs" shape="pill" data-testid="cms-archive-trigger">
          {t('cms.archive.trigger')}
        </Button>
      </Sheet.Trigger>
      <Sheet.Content data-testid="cms-archive-drawer">
        <Sheet.Title>{t('cms.archive.title')}</Sheet.Title>
        <Sheet.Description>{t('cms.archive.description')}</Sheet.Description>
        {status.kind === 'error' ? (
          <p role="alert" className={errorLineClasses}>
            {t('cms.archive.restoreError')}
          </p>
        ) : null}
        <ArchiveList
          // A success clears a stale failure line; the success itself is
          // announced once, by the toast. This drawer used to say the same
          // sentence into its own `aria-live` line, and two live regions
          // carrying one sentence read it out twice.
          onRestored={() => setStatus({ kind: 'idle' })}
          onRestoreError={() => setStatus({ kind: 'error' })}
        />
      </Sheet.Content>
    </Sheet.Root>
  );
}
